import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { IInstance, IWizardProps } from "../types";

export function useWizardProvider() {
  const [instances, setInstances] = useState<IInstance[]>([]);
  const [isLoading, setIsLoading] = useState<Boolean>(false);
  const location = useLocation();

  // close all wizards when route is changed
  useEffect(() => {
    if (instances.length > 0) {
      setInstances([]);
      setIsLoading(false);
    }
  }, [location]);

  const setStepValidity = useCallback(
    (instanceId: number, stepIndex: number, isValid: boolean) => {
      setInstances((currentInstances) =>
        currentInstances.map((instance) => {
          if (instance.id !== instanceId) {
            return instance;
          }

          const modifiedSteps = [...instance.steps];

          modifiedSteps[stepIndex] = {
            ...modifiedSteps[stepIndex],
            isInvalid: !isValid,
          };

          return {
            ...instance,
            steps: modifiedSteps,
          };
        }),
      );
    },
    [setInstances],
  );

  const createInstance = useCallback((config: IWizardProps) => {
    const {
      submitButtonText,
      submitButtonColor,
      cancelButtonText,
      steps,
      saveCallback,
      getButtonConfigs,
      resolveCallback,
      mode,
      initialData,
      showSuccessNotification,
    } = config;

    setInstances((currentInstances) => [
      ...currentInstances,
      {
        id: Date.now(),
        submitButtonText,
        cancelButtonText,
        saveCallback: saveCallback || null,
        getButtonConfigs: getButtonConfigs || null,
        resolveCallback,
        submitButtonColor,
        steps: steps.map((step) => ({ ...step })),
        mode,
        initialData,
        showSuccessNotification,
      },
    ]);
  }, []);

  const deleteInstance = useCallback((id) => {
    setInstances((currentInstances) =>
      currentInstances.filter((instance) => instance.id !== id),
    );
  }, []);

  return {
    data: {
      isLoading,
      instances,
    },
    actions: {
      createInstance,
      setIsLoading,
      setStepValidity,
      deleteInstance,
    },
  };
}
